import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface User {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  storage_limit_mb: number;
  storage_used_mb: number;
  ai_requests_limit: number;
  ai_requests_used: number;
  storage_notified_50: boolean;
  ai_notified_50: boolean;
  board_count: number;
  item_count: number;
}

interface WaitlistEntry {
  id: string;
  email: string;
  status: 'pending' | 'invited' | 'converted';
  invited_at: string | null;
  created_at: string;
}

interface PlatformStats {
  totalUsers: number;
  waitlistCount: number;
  totalStorageMb: number;
  totalAiRequests: number;
  totalBoards: number;
  totalItems: number;
}

export function useAdminApi() {
  const [isLoading, setIsLoading] = useState(false);

  const callAdminApi = useCallback(async (action: string, data?: Record<string, unknown>) => {
    setIsLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        throw new Error('Not authenticated');
      }

      const response = await supabase.functions.invoke('admin-operations', {
        body: { action, data }
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      return response.data;
    } catch (error) {
      console.error('Admin API error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getUsers = useCallback(async (): Promise<User[]> => {
    const result = await callAdminApi('getUsers');
    return result.users;
  }, [callAdminApi]);

  const createUser = useCallback(async (
    email: string,
    password: string,
    storageLimit: number,
    aiLimit: number,
    fromWaitlist: boolean = false,
    sendWelcomeEmail: boolean = true
  ) => {
    const result = await callAdminApi('createUser', { 
      email, 
      password, 
      storageLimit, 
      aiLimit, 
      fromWaitlist,
      sendWelcomeEmail 
    });
    const emailNote = sendWelcomeEmail ? ' - Welcome email sent!' : '';
    toast.success(`User created: ${email}${emailNote}`);
    return result.user;
  }, [callAdminApi]);

  const updateUser = useCallback(async (
    userId: string,
    updates: { storageLimit?: number; aiLimit?: number; resetStorage?: boolean; resetAI?: boolean }
  ) => {
    await callAdminApi('updateUser', { userId, ...updates });
    toast.success('User updated');
  }, [callAdminApi]);

  const resetPassword = useCallback(async (userId: string, newPassword: string) => {
    await callAdminApi('resetPassword', { userId, newPassword });
    toast.success('Password reset successfully');
  }, [callAdminApi]);

  const deleteUser = useCallback(async (userId: string, email: string) => {
    await callAdminApi('deleteUser', { userId });
    toast.success(`User deleted: ${email}`);
  }, [callAdminApi]);

  const getWaitlist = useCallback(async (): Promise<WaitlistEntry[]> => {
    const result = await callAdminApi('getWaitlist');
    return result.waitlist;
  }, [callAdminApi]);

  const deleteWaitlistEntry = useCallback(async (id: string) => {
    await callAdminApi('deleteWaitlistEntry', { id });
    toast.success('Waitlist entry deleted');
  }, [callAdminApi]);

  const getStats = useCallback(async (): Promise<PlatformStats> => {
    const result = await callAdminApi('getStats');
    return result.stats;
  }, [callAdminApi]);

  const updateAllUsersLimits = useCallback(async (
    storageLimit?: number,
    aiLimit?: number
  ) => {
    await callAdminApi('updateAllUsersLimits', { storageLimit, aiLimit });
    toast.success('All user limits updated');
  }, [callAdminApi]);

  return {
    isLoading,
    getUsers,
    createUser,
    updateUser,
    resetPassword,
    deleteUser,
    getWaitlist,
    deleteWaitlistEntry,
    getStats,
    updateAllUsersLimits
  };
}
